import type { StoreSettings } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  CheckboxField,
  PageHeader,
  TextAreaField,
  TextField,
} from '@avrahami/ui';
import { useCan } from '../api/session';
import { FormStatus } from '../components/fields';
import { useSettingsEditor, WithSettings } from '../components/settings';
import { StoreMailCard } from '../components/StoreMailCard';
import { StaleNotice } from '../components/versioned';
import { he } from '../i18n/he';

export function SettingsPage() {
  const canWrite = useCan('store.settings.write');
  return (
    <>
      <PageHeader title={he.settings.title} />
      <WithSettings>
        {(settings) => (
          <div className="flex flex-col gap-6">
            <SettingsForm settings={settings} canWrite={canWrite} />
            <StoreMailCard canWrite={canWrite} />
          </div>
        )}
      </WithSettings>
    </>
  );
}

/** Every store setting, grouped; saved together as one replacement of the settings object. */
function SettingsForm({ settings, canWrite }: { settings: StoreSettings; canWrite: boolean }) {
  const { draft, set, submit, errors, localInvalid, save, stale, reload } =
    useSettingsEditor(settings);
  return (
    <form onSubmit={submit} noValidate data-testid="settings-form">
      <fieldset disabled={!canWrite} className="flex flex-col gap-6">
        <StaleNotice stale={stale} onReload={reload} />
        {canWrite ? null : <Alert tone="info">{he.common.readOnly}</Alert>}
        <Card title={he.settings.store}>
          <div className="grid gap-4 sm:grid-cols-2">
            <CheckboxField
              label={he.settings.siteOpen}
              hint={he.settings.siteOpenHint}
              checked={draft.siteOpen}
              onChange={(event) => {
                set({ siteOpen: event.target.checked });
              }}
            />
            <CheckboxField
              label={he.settings.ordersEnabled}
              hint={he.settings.ordersEnabledHint}
              checked={draft.ordersEnabled}
              onChange={(event) => {
                set({ ordersEnabled: event.target.checked });
              }}
            />
            <CheckboxField
              label={he.settings.guestCheckout}
              hint={he.settings.guestCheckoutHint}
              checked={draft.guestCheckoutEnabled}
              onChange={(event) => {
                set({ guestCheckoutEnabled: event.target.checked });
              }}
            />
            <CheckboxField
              label={he.settings.registration}
              checked={draft.registrationEnabled}
              onChange={(event) => {
                set({ registrationEnabled: event.target.checked });
              }}
            />
            <CheckboxField
              label={he.settings.showPrices}
              hint={he.settings.showPricesHint}
              data-testid="settings-show-prices"
              checked={draft.showPrices}
              onChange={(event) => {
                set({ showPrices: event.target.checked });
              }}
            />
          </div>
        </Card>

        <Card title={he.settings.business}>
          <p className="mb-4 text-sm text-slate-600" data-testid="settings-business-hint">
            {he.settings.businessHint}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label={he.settings.legalName}
              name="legalName"
              hint={he.settings.legalNameHint}
              maxLength={120}
              value={draft.legalName}
              error={errors['business.legalName']}
              onChange={(event) => {
                set({ legalName: event.target.value });
              }}
            />
            <TextField
              label={he.settings.businessId}
              name="businessId"
              dir="ltr"
              inputMode="numeric"
              maxLength={9}
              value={draft.businessId}
              error={errors['business.businessId']}
              onChange={(event) => {
                set({ businessId: event.target.value });
              }}
            />
            <TextField
              label={he.settings.phone}
              name="phone"
              type="tel"
              dir="ltr"
              maxLength={20}
              hint={he.settings.phoneHint}
              value={draft.phone}
              error={errors['business.phone']}
              onChange={(event) => {
                set({ phone: event.target.value });
              }}
            />
            <TextField
              label={he.settings.whatsapp}
              name="whatsapp"
              type="tel"
              dir="ltr"
              maxLength={20}
              hint={he.settings.whatsappHint}
              value={draft.whatsapp}
              error={errors['business.whatsapp']}
              onChange={(event) => {
                set({ whatsapp: event.target.value });
              }}
            />
            <TextField
              label={he.settings.email}
              name="email"
              type="email"
              dir="ltr"
              maxLength={254}
              value={draft.email}
              error={errors['business.email']}
              onChange={(event) => {
                set({ email: event.target.value });
              }}
            />
            <TextField
              label={he.settings.address}
              name="address"
              maxLength={300}
              value={draft.address}
              error={errors['business.address']}
              onChange={(event) => {
                set({ address: event.target.value });
              }}
            />
            <TextField
              label={he.settings.website}
              name="website"
              type="url"
              dir="ltr"
              maxLength={300}
              value={draft.website}
              error={errors['business.website']}
              onChange={(event) => {
                set({ website: event.target.value });
              }}
            />
          </div>
        </Card>

        <Card title={he.settings.seo}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">{he.settings.seoHint}</p>
            <TextField
              label={he.settings.seoTitle}
              name="seoTitle"
              maxLength={70}
              value={draft.seoTitle}
              error={errors['seo.title']}
              onChange={(event) => {
                set({ seoTitle: event.target.value });
              }}
            />
            <TextAreaField
              label={he.settings.seoDescription}
              name="seoDescription"
              rows={3}
              maxLength={320}
              value={draft.seoDescription}
              error={errors['seo.description']}
              onChange={(event) => {
                set({ seoDescription: event.target.value });
              }}
            />
          </div>
        </Card>

        <FormStatus
          error={save.error}
          localInvalid={localInvalid}
          saved={save.isSuccess}
          onReload={reload}
        />
        {canWrite ? (
          <div>
            <Button type="submit" busy={save.isPending}>
              {he.common.save}
            </Button>
          </div>
        ) : null}
      </fieldset>
    </form>
  );
}
